import React, { useEffect } from 'react';

import {
	Captions,
	Headline,
	SubHeadline,
	Text,
	Title,
} from '~components/Typography';
import { PaymentType } from '~context/generalPaywall/types';
import annalsCheckout from '~features/annals/hooks/checkout';
import type {
	BenefitsModalProps,
	TBenefit,
} from '~features/benefits/model/types';
import { useTranslations } from '~i18n/hooks';
import CloseIcon from '~public/assets/icons/close.svg';
import { ButtonPrimary } from '~shared/ui/atoms/Buttons/ButtonPrimary';
import { NeuronImage } from '~shared/ui/atoms/NeuronImage';
import { crossedOutContent } from '~theme/snippets';

import {
	ActionsWrapper,
	BenefitsContent,
	BenefitsList,
	BenefitsWrapper,
	CloseButton,
	Divider,
	FirstColumn,
	HighlightedTitle,
	ThirdColumn,
	TotalWrapper,
	WarnButton,
	Wrapper,
} from './styles';

export const Content = ({
	onOk,
	onCancel,
	reason,
	reasonType,
}: BenefitsModalProps) => {
	const t = useTranslations('BenefitsModal');

	const sendDecline = annalsCheckout.useBeginCheckoutPopupClickDecline({
		type: PaymentType.Subscription,
		reason,
		reasonType,
	});

	const sendShow = annalsCheckout.useBeginCheckoutPopupShow({
		type: PaymentType.Subscription,
		reason,
		reasonType,
	});

	const sendOk = annalsCheckout.useBeginCheckoutPopupClickBack({
		type: PaymentType.Subscription,
		reason,
		reasonType,
	});

	const handleCancel = () => {
		sendDecline();
		onCancel();
	};

	const handleOk = () => {
		sendOk();
		onOk();
	};

	useEffect(() => {
		sendShow();
	}, [sendShow]);

	return (
		<BenefitsContent>
			<CloseButton leftIcon={<CloseIcon />} onClick={handleCancel} />
			<div>
				<Title size="M">{t('title')}</Title>
				<Title size="M">{t('secondTitle')}</Title>
			</div>
			<Wrapper>
				<HighlightedTitle size="M">
					{t('listHighlightedTitle')}
				</HighlightedTitle>
				<BenefitsWrapper>
					<SubHeadline size="M">{t('listTitle')}</SubHeadline>
					<BenefitsList>
						{(t.raw('benefits') as TBenefit[]).map(
							({ firstColumn, showNeuron, secondColumn, thirdColumn }) => (
								<React.Fragment key={secondColumn}>
									<FirstColumn size="M">{firstColumn}</FirstColumn>
									<Text align="left" size="M">
										{secondColumn}
									</Text>
									<ThirdColumn>
										<SubHeadline>{thirdColumn}</SubHeadline>
										{showNeuron && <NeuronImage />}
									</ThirdColumn>
								</React.Fragment>
							)
						)}
					</BenefitsList>
					<Divider />
					<TotalWrapper>
						<Text align="left" size="M">
							{t('totalTitle')}
						</Text>
						<SubHeadline
							className={crossedOutContent}
							size="S"
							color="grey-52-solid"
						>
							{t('totalOldPrice')}
						</SubHeadline>
						<Headline color="rose-58-solid" size="S">
							{t('totalNewPrice')}
						</Headline>
						<Text size="M">{t('totalText')}</Text>
						<SubHeadline size="S">{t('neuronCount')}</SubHeadline>
						<NeuronImage />
					</TotalWrapper>
				</BenefitsWrapper>
			</Wrapper>
			<ActionsWrapper>
				<ButtonPrimary onClick={handleOk} size="M">
					{t('firstButtonText')}
				</ButtonPrimary>
				<WarnButton onClick={handleCancel} size="M">
					{t('secondButtonText')}
				</WarnButton>
			</ActionsWrapper>
			<Captions size="L" color="grey-52-solid">
				{t('actionLabel')}
			</Captions>
		</BenefitsContent>
	);
};
